/**
 * Icon Cache Service (Web Version)
 * 
 * Caches external images (with SAS tokens) as blob URLs to prevent 403 errors
 * from expired tokens. Web version uses IndexedDB for persistent caching.
 * 
 * Supports pack: URL resolution via Marketplace Function API.
 */

import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { config } from '../config/environment';

const ICON_CACHE_NAME = 'aitryx-icon-cache';
const CACHE_VERSION = 2; // Bumped for pack: URL support

// Marketplace API URL from centralized config
const MARKETPLACE_API_URL = config.api.marketplaceUrl;

interface CacheEntry {
  url: string;
  blob: Blob;
  timestamp: number;
}

// Cache for pack: URL resolution - maps pack path to SAS URL
const packAssetIndex: Record<string, Record<string, string>> = {};

// Memory cache for already-resolved URLs in this session
// Prevents repeated IndexedDB lookups and reduces log spam
const resolvedUrlCache: Record<string, string> = {};

/**
 * Open IndexedDB for icon caching
 */
async function openCache(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ICON_CACHE_NAME, CACHE_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('icons')) {
        db.createObjectStore('icons', { keyPath: 'url' });
      }
    };
  });
}

/**
 * Get cached icon from IndexedDB
 */
async function getCachedBlob(url: string): Promise<Blob | null> {
  try {
    const db = await openCache();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['icons'], 'readonly');
      const store = transaction.objectStore('icons');
      const request = store.get(url);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined;
        resolve(entry?.blob || null);
      };
    });
  } catch (e) {
    console.warn('Failed to get cached blob:', e);
    return null;
  }
}

/**
 * Store blob in IndexedDB cache
 */
async function setCachedBlob(url: string, blob: Blob): Promise<void> {
  try {
    const db = await openCache();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['icons'], 'readwrite');
      const store = transaction.objectStore('icons');
      const entry: CacheEntry = {
        url,
        blob,
        timestamp: Date.now()
      };
      const request = store.put(entry);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('Failed to cache blob:', e);
  }
}

/**
 * Load asset index from Marketplace Function for a category.
 * Returns a map of "subcategory/filename" -> SAS URL
 */
async function loadPackAssetsIndex(category: string): Promise<Record<string, string>> {
  const cat = (category || 'software').toLowerCase();
  if (packAssetIndex[cat]) return packAssetIndex[cat];
  
  try {
    const token = await ensureFreshAitryxCoreToken();
    const api = `${MARKETPLACE_API_URL}/assets/partner/category/${encodeURIComponent(cat)}`;
    
    const response = await fetch(api, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      packAssetIndex[cat] = {};
      return packAssetIndex[cat];
    }
    
    const json = await response.json();
    const index: Record<string, string> = {};
    
    // Response format: { name, displayName, subcategories: [{ name, assets: [...] }] }
    const subs = (json?.subcategories || []) as Array<{ name: string; assets: Array<any> }>;
    subs.forEach(sub => {
      const subName = sub.name;
      sub.assets?.forEach((a: any) => {
        if (a?.filename && a?.url) {
          const key = `${subName}/${a.filename}`.replace(/^\//, '').toLowerCase();
          index[key] = a.url;
        }
      });
    });
    
    packAssetIndex[cat] = index;
    return index;
  } catch (e) {
    packAssetIndex[cat] = {};
    return packAssetIndex[cat];
  }
}

/**
 * Resolve a pack: URL to a SAS URL.
 * Format: pack:subcategory/filename (e.g., pack:microsoft/M365.png)
 */
async function resolvePackUrl(packUrl: string): Promise<string | null> {
  const packPath = packUrl.substring('pack:'.length).replace(/^\//, '');
  const key = packPath.toLowerCase();
  
  // Try common categories
  const tryCategories = ['software', 'hardware', 'generic', 'telstra', 'custom'];
  
  for (const cat of tryCategories) {
    const index = await loadPackAssetsIndex(cat);
    if (index[key]) {
      return index[key];
    }
  }
  
  return null;
}

/**
 * Fetch an icon and cache it locally. Returns a local object URL to display.
 * Supports both regular HTTP URLs and pack: URLs.
 */
export async function getCachedIcon(url: string): Promise<string | null> {
  try {
    // Check memory cache first (fastest)
    if (resolvedUrlCache[url]) {
      return resolvedUrlCache[url];
    }
    
    let actualUrl = url;
    
    // Resolve pack: URLs to SAS URLs
    if (url.startsWith('pack:')) {
      const sasUrl = await resolvePackUrl(url);
      if (!sasUrl) {
        return null;
      }
      actualUrl = sasUrl;
    }

    // Check IndexedDB cache (use original URL as key for consistency)
    let blob = await getCachedBlob(url);
    
    // If not cached, fetch and cache it
    if (!blob) {
      const response = await fetch(actualUrl);
      if (!response.ok) {
        console.warn(`Failed to fetch icon: ${response.status} ${response.statusText}`);
        return null;
      }
      
      blob = await response.blob();
      await setCachedBlob(url, blob); // Cache with original URL key
    }

    // Create and return object URL
    const objectUrl = URL.createObjectURL(blob);
    resolvedUrlCache[url] = objectUrl; // Store in memory cache
    return objectUrl;
  } catch (e) {
    console.warn('Icon cache fetch failed:', e);
    return null;
  }
}

/**
 * Preload and cache a set of icon URLs. Returns a map of url -> objectUrl.
 */
export async function preloadIcons(urls: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  
  // Process in parallel for better performance
  await Promise.all(
    urls.map(async (url) => {
      const objectUrl = await getCachedIcon(url);
      if (objectUrl) {
        out[url] = objectUrl;
      }
    })
  );
  
  return out;
}

/**
 * Revoke all held object URLs and clear the memory cache.
 * Call on logout or when navigating away from image-heavy views.
 */
export function revokeAllObjectUrls(): void {
  for (const url of Object.values(resolvedUrlCache)) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
  for (const key of Object.keys(resolvedUrlCache)) {
    delete resolvedUrlCache[key];
  }
}

/**
 * Clear the icon cache (useful for troubleshooting)
 */
export async function clearIconCache(): Promise<void> {
  try {
    revokeAllObjectUrls();
    const db = await openCache();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['icons'], 'readwrite');
      const store = transaction.objectStore('icons');
      const request = store.clear();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('Failed to clear icon cache:', e);
  }
}

